console.log('Vite ⚡️ Rails')

// import ActiveStorage from '@rails/activestorage'
// ActiveStorage.start()
import "trix"
import "@rails/actiontext"

import {Turbo} from "@hotwired/turbo-rails"
document.addEventListener('turbo:load', ()=>{
	console.log("Turbo loaded!")

	if (isSafari26()) {
		showSafari26Warning()
	}
})

import '../controllers'

import 'chartkick/chart.js'

// https://marcoroth.dev/posts/guide-to-custom-turbo-stream-actions
Turbo.StreamActions.redirect = function() {
	let url = this.getAttribute("url")
	let parsedUrl = new URL(url);

	if (parsedUrl.origin === window.location.origin || isExternalUrlAllowed(parsedUrl)) {
		Turbo.visit(url);
	} else {
		console.log("Redirection to this URL is not allowed.");
	}
}

Turbo.StreamActions.scroll_to = function () {
  const target = this.targetElements[0];
  target.scrollIntoView({behavior: "smooth"});
};

// Jobs broadcast this once per finished document: coalesce a burst into one fetch, and
// abort a superseded request so an outdated response never renders last.
const FETCH_STREAMS_DEBOUNCE_MS = 150;
const pendingStreamFetches = new Map();

Turbo.StreamActions.fetch_streams = function () {
  const src = this.getAttribute("src");
  const previous = pendingStreamFetches.get(src);
  if (previous) {
    clearTimeout(previous.timer);
    previous.abortController.abort();
  }

  const pending = { abortController: new AbortController() };
  pending.timer = setTimeout(async () => {
    try {
      const response = await fetch(src, {
        headers: { Accept: "text/vnd.turbo-stream.html" },
        signal: pending.abortController.signal
      });
      if (response.ok) Turbo.renderStreamMessage(await response.text());
    } catch (error) {
      if (error.name !== "AbortError") throw error;
    } finally {
      if (pendingStreamFetches.get(src) === pending) pendingStreamFetches.delete(src);
    }
  }, FETCH_STREAMS_DEBOUNCE_MS);
  pendingStreamFetches.set(src, pending);
};

Turbo.StreamActions.update_tree_data = function () {
	const target = this.targetElements[0];
	if (!target) return;

	const payload = this.templateElement?.content?.textContent?.trim();
	if (!payload) return;

	try {
		const nextData = JSON.parse(payload);
		const treeController = window.Stimulus?.getControllerForElementAndIdentifier(target, "tree");

		if (treeController) {
			treeController.dataValue = nextData;
			return;
		}

		target.setAttribute("data-tree-data-value", JSON.stringify(nextData));
	} catch (error) {
		console.error("Failed to update tree data", error);
	}
};

Turbo.StreamActions.append_tree_node = function () {
	const target = this.targetElements[0];
	if (!target) return;

	const payload = this.templateElement?.content?.textContent?.trim();
	if (!payload) return;

	try {
		const { parentId, node } = JSON.parse(payload);
		const treeController = window.Stimulus?.getControllerForElementAndIdentifier(target, "tree");

		if (treeController?.appendChildNode) {
			treeController.appendChildNode(parentId, node);
		}
	} catch (error) {
		console.error("Failed to append tree node", error);
	}
};

Turbo.StreamActions.update_tree_node = function () {
	updateTreeNodeFromStreamAction(this);
};

Turbo.StreamActions.update_tree_node_if_unfocused = function () {
	updateTreeNodeFromStreamAction(this, { ifUnfocused: true });
};

function updateTreeNodeFromStreamAction(streamAction, options = {}) {
	const target = streamAction.targetElements[0];
	if (!target) return;

	if (options.ifUnfocused) {
		const activeElement = document.activeElement;
		if (activeElement && target.contains(activeElement)) return;
	}

	const payload = streamAction.templateElement?.content?.textContent?.trim();
	if (!payload) return;

	try {
		const { node } = JSON.parse(payload);
		const treeController = window.Stimulus?.getControllerForElementAndIdentifier(target, "tree");

		if (treeController?.updateNode) {
			treeController.updateNode(node);
		}
	} catch (error) {
		console.error("Failed to update tree node", error);
	}
}

Turbo.StreamActions.remove_tree_node = function () {
	const target = this.targetElements[0];
	if (!target) return;

	const payload = this.templateElement?.content?.textContent?.trim();
	if (!payload) return;

	try {
		const { nodeId } = JSON.parse(payload);
		const treeController = window.Stimulus?.getControllerForElementAndIdentifier(target, "tree");

		if (treeController?.removeNode) {
			treeController.removeNode(nodeId);
		}
	} catch (error) {
		console.error("Failed to remove tree node", error);
	}
};

Turbo.StreamActions.insert_tree_level = function () {
	const target = this.targetElements[0];
	if (!target) return;

	const payload = this.templateElement?.content?.textContent?.trim();
	if (!payload) return;

	try {
		const { childIds, node } = JSON.parse(payload);
		const treeController = window.Stimulus?.getControllerForElementAndIdentifier(target, "tree");

		if (treeController?.insertLevelNode) {
			treeController.insertLevelNode(childIds, node);
		}
	} catch (error) {
		console.error("Failed to insert tree level", error);
	}
};

Turbo.StreamActions.unwrap_tree_node = function () {
	const target = this.targetElements[0];
	if (!target) return;

	const payload = this.templateElement?.content?.textContent?.trim();
	if (!payload) return;

	try {
		const { nodeId } = JSON.parse(payload);
		const treeController = window.Stimulus?.getControllerForElementAndIdentifier(target, "tree");

		if (treeController?.unwrapNode) {
			treeController.unwrapNode(nodeId);
		}
	} catch (error) {
		console.error("Failed to unwrap tree node", error);
	}
};

Turbo.StreamActions.update_tree_node_template = function () {
	updateTreeNodeTemplateFromStreamAction(this);
};

Turbo.StreamActions.update_tree_node_template_if_unfocused = function () {
	updateTreeNodeTemplateFromStreamAction(this, { ifUnfocused: true });
};

function updateTreeNodeTemplateFromStreamAction(streamAction, options = {}) {
	const target = streamAction.targetElements[0];
	if (!target) return;

	if (options.ifUnfocused) {
		const activeElement = document.activeElement;
		if (activeElement && target.contains(activeElement)) return;
	}

	const payload = streamAction.templateElement?.content?.textContent?.trim();
	if (!payload) return;

	try {
		const { nodeTemplates } = JSON.parse(payload);
		const treeController = window.Stimulus?.getControllerForElementAndIdentifier(target, "tree");

		if (treeController?.updateNodeTemplates) {
			treeController.updateNodeTemplates(nodeTemplates);
			return;
		}

		if (!nodeTemplates || typeof nodeTemplates !== "object") {
			return;
		}

		const currentTemplates = JSON.parse(target.getAttribute("data-tree-node-templates-value") || "{}");
		Object.assign(currentTemplates, nodeTemplates);
		target.setAttribute("data-tree-node-templates-value", JSON.stringify(currentTemplates));
	} catch (error) {
		console.error("Failed to update tree node template", error);
	}
}

// Rebuilds the agent selects of the structure manager table rows from the refreshed option sets
// (keyed "<agent group id>:<merge candidates allowed>"), keeping each row's own agents.
Turbo.StreamActions.update_agent_rank_select_options = function () {
	const target = this.targetElements[0];
	if (!target) return;

	const payload = this.templateElement?.content?.textContent?.trim();
	if (!payload) return;

	try {
		const { optionSets } = JSON.parse(payload);
		if (!optionSets || typeof optionSets !== "object") return;

		target.querySelectorAll("select[data-agent-rank-options-key]").forEach((select) => {
			const sharedOptions = optionSets[select.dataset.agentRankOptionsKey];
			if (!Array.isArray(sharedOptions)) return;

			rebuildAgentRankSelect(select, sharedOptions);
		});
	} catch (error) {
		console.error("Failed to update agent rank select options", error);
	}
};

function rebuildAgentRankSelect(select, sharedOptions) {
	const slimSelectController = window.Stimulus?.getControllerForElementAndIdentifier(select, "slim-select");
	// Leave a select the user is currently picking from alone.
	if (slimSelectController?.slimselect?.settings?.isOpen) return;

	const ownOptions = JSON.parse(select.dataset.agentRankOwnOptions || "[]");
	const selectedValue = select.value;
	const blankOption = select.querySelector('option[value=""]');
	const seenIds = new Set();
	const options = [...ownOptions, ...sharedOptions].filter(([, id]) => {
		if (seenIds.has(String(id))) return false;
		seenIds.add(String(id));
		return true;
	});

	select.replaceChildren(
		...(blankOption ? [blankOption] : []),
		...options.map(([name, id]) => new Option(name, id, false, String(id) === selectedValue)),
	);
	slimSelectController?.attach();
}

Turbo.StreamActions.replace_if_unfocused = function () {
	const activeElement = document.activeElement;

	this.targetElements.forEach((target) => {
		if (!target) return;

		// Keep the current row stable while the user is actively editing it.
		if (activeElement && target.contains(activeElement)) return;

		target.replaceWith(this.templateContent.cloneNode(true));
	});
};

function isExternalUrlAllowed(url) {
  const allowedHosts = ['www.nettowelt.info'];

  return allowedHosts.includes(url.hostname);
}

// https://github.com/RoleModel/turbo-confirm
import TC from "@rolemodel/turbo-confirm"

TC.start()

function isSafari26() {
	if (typeof navigator === "undefined") return false;

	const ua = navigator.userAgent || "";
	const isSafari = ua.includes("Safari") && !ua.includes("Chrome") && !ua.includes("CriOS") && !ua.includes("Chromium");
	if (!isSafari) return false;

	const match = ua.match(/Version\/(\d+(?:\.\d+)?)/);
	if (!match) return false;

	const version = match[1];
  console.log("Detected Safari version:", version);
	// Only block Safari exactly at version 26.0
	return version === "26.0";
}

function showSafari26Warning() {
	const message = "Ihre aktuelle Safari-Version (26.0) hat bekannte Fehler. Bitte aktualisieren Sie Ihren Browser, um diese Webseite nutzen zu können.";

	const overlay = document.createElement("div");
	overlay.setAttribute("id", "safari-26-warning");
	overlay.style.position = "fixed";
	overlay.style.inset = "0";
	overlay.style.zIndex = "99999";
	overlay.style.background = "#111827";
	overlay.style.color = "#F9FAFB";
	overlay.style.display = "flex";
	overlay.style.flexDirection = "column";
	overlay.style.alignItems = "center";
	overlay.style.justifyContent = "center";
	overlay.style.padding = "1.5rem";
	overlay.style.textAlign = "center";
	overlay.style.fontFamily = "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

	const title = document.createElement("h1");
	title.textContent = "Browser-Update erforderlich";
	title.style.fontSize = "1.5rem";
	title.style.marginBottom = "0.75rem";

	const text = document.createElement("p");
	text.textContent = message;
	text.style.maxWidth = "36rem";
	text.style.marginBottom = "0.75rem";

	const hint = document.createElement("p");
	hint.textContent = "Bitte nutzen Sie ein Safari-Update oder einen aktuellen Browser wie Chrome, Firefox, Edge oder Safari in einer neueren Version.";
	hint.style.maxWidth = "36rem";
	hint.style.fontSize = "0.875rem";
	hint.style.opacity = "0.8";

	overlay.appendChild(title);
	overlay.appendChild(text);
	overlay.appendChild(hint);
	document.body.appendChild(overlay);
}
